// sim/geneExpr.ts — gene EXPRESSION magnitude bounding (the soft-cap safety net).
//
// WHY THIS EXISTS:
//   expressGene sums a gene's honest, UNCLAMPED integer channel contributions.
//   Trait stacking is additive and splicing sums BOTH parents' stacks into the
//   child, so repeated autonomous splicing (rivals.evolve / colonyAI.evolve /
//   the hive Mutation chamber) can drive a single gene's stack — and therefore
//   one channel — into the thousands (a Legendary, deeply-spliced gene). Those
//   magnitudes are honest but absurd; this is the long-parked "magnitude
//   clamping" seam (docs/BACKLOG.md, DESIGN.md §8).
//
// THE CURVE (integer, sign-preserving, monotonic):
//   Up to SOFT_CAP_THRESHOLD a magnitude passes through UNCHANGED — so every
//   normal/small gene (and every value any existing test or short default run
//   produces) is byte-identical to the pre-cap output. Past the threshold the
//   EXCESS is compressed (halved) and the result is hard-clamped at
//   SOFT_CAP_CEILING, so a pathological stack is bounded instead of exploding.
//   Diminishing returns, not a hard wall at the threshold: a gene a little over
//   the threshold still expresses a little more than one exactly at it.
//
// This is a pure, allocation-free integer function. It is deliberately small and
// self-contained so it can be unit-tested in isolation (geneExpr.test.mjs).

/**
 * Magnitude (absolute value) at/below which a channel value is returned
 * UNCHANGED. Chosen well above anything short default play or any existing test
 * produces (a single forged gene tops out near ~40 only at max rarity + a full
 * Volatile stack; normal genes are single digits), so the cap is a pure safety
 * net for the runaway splice case and never perturbs ordinary expression.
 */
export const SOFT_CAP_THRESHOLD = 24;

/**
 * Hard ceiling on a single channel's expressed magnitude after compression. No
 * matter how deeply a gene is spliced, one channel can contribute at most this
 * (per sign). Keeps colony-summed profiles and any downstream argmax sane.
 */
export const SOFT_CAP_CEILING = 48;

/**
 * Soft-cap one signed channel value with diminishing returns above the
 * threshold, hard-clamped at the ceiling. Integer math, sign-preserving:
 *   |v| <= THRESHOLD            ⇒ v unchanged
 *   THRESHOLD < |v|            ⇒ sign * min(CEILING, THRESHOLD + (|v|-THRESHOLD)/2)
 * Monotonic non-decreasing in |v|; allocation-free.
 */
export function softCapChannel(v: number): number {
  const m = v < 0 ? -v : v;
  if (m <= SOFT_CAP_THRESHOLD) return v;
  let capped = SOFT_CAP_THRESHOLD + ((m - SOFT_CAP_THRESHOLD) >> 1);
  if (capped > SOFT_CAP_CEILING) capped = SOFT_CAP_CEILING;
  return v < 0 ? -capped : capped;
}
